import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SalesPeriodState } from "../../hooks/useSalesPeriodState";
import { useGetAdminMrrStatsQuery } from "../../query/adminMrrStatsQuery";
import {
  formatSalesDateLabel,
  getComparisonDate,
  getComparisonPeriod,
} from "../../utils/dailySalesUtils";
import { SectionTitle } from "../../components/SectionTitle";
import { MrrValueBoxes } from "./MrrValueBoxes";
import { MrrChart } from "./MrrChart";

interface MrrStatsSectionProps {
  periodState: SalesPeriodState;
}

export const MrrStatsSection = ({ periodState }: MrrStatsSectionProps) => {
  const { t } = useTranslation();
  const {
    period,
    periodPreset,
    selectedDate,
    selectedDateLabel,
    handleSelectDate,
  } = periodState;
  const comparisonPeriod = useMemo(
    () => getComparisonPeriod(periodPreset, period),
    [periodPreset, period]
  );
  const comparisonDate = getComparisonDate(selectedDate, periodPreset, period);

  const { data: stats, isLoading } = useGetAdminMrrStatsQuery(
    period.from,
    period.to
  );
  const { data: comparisonStats } = useGetAdminMrrStatsQuery(
    comparisonPeriod.from,
    comparisonPeriod.to
  );

  return (
    <>
      <SectionTitle>{t("adminMrrStats.sectionTitle")}</SectionTitle>
      <MrrValueBoxes
        date={selectedDate}
        selectedDateLabel={selectedDateLabel}
        comparisonDateLabel={formatSalesDateLabel(comparisonDate)}
        dayStats={stats?.find((day) => day.date === selectedDate)}
        comparisonStats={comparisonStats?.find(
          (day) => day.date === comparisonDate
        )}
      />
      <MrrChart
        period={period}
        periodPreset={periodPreset}
        stats={stats}
        comparisonStats={comparisonStats}
        isLoading={isLoading}
        onSelectDate={handleSelectDate}
      />
    </>
  );
};
